import { useMemo } from 'react';

import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { BetSubmittedState, ComboBetTypes } from '@/feature/betslip-sbk/types';
import {
    adjustedPayout,
    calcProfitBoost,
    getComboBetTypeFromGroupedSelections,
} from '@/feature/betslip-sbk/utils/betslip-utils';

export type EntryType = 'single' | 'parlay' | 'sgp' | 'sgp_plus' | 'multiple';

export type BetSuccessData = {
    entryType: EntryType;
    pickCount: number;
    payoutAmount: number;
    betIds: string[];
    hasCombo: boolean;
};

/**
 * Determines if this is a "multiple entries" scenario
 * Multiple entries: 2+ singles OR (1+ singles AND 1 combo)
 */
const isMultipleEntriesCheck = (submittedState: BetSubmittedState): boolean => {
    const singlesCount = submittedState.singlesBets.length;
    const hasCombo = !!submittedState.comboBet;

    return singlesCount >= 2 || (singlesCount >= 1 && hasCombo);
};

/**
 * Determines the entry type for display purposes
 */
const getEntryType = (submittedState: BetSubmittedState): EntryType => {
    if (isMultipleEntriesCheck(submittedState)) {
        return 'multiple';
    }

    if (submittedState.comboBet) {
        // Group selections by event and get counts
        const selectionCountsByEvent: Record<string, number> = {};
        for (const selection of submittedState.comboBet.selections) {
            selectionCountsByEvent[selection.eventId] = (selectionCountsByEvent[selection.eventId] ?? 0) + 1;
        }
        const comboBetType = getComboBetTypeFromGroupedSelections(Object.values(selectionCountsByEvent));
        switch (comboBetType) {
            case ComboBetTypes.Parlay:
                return 'parlay';
            case ComboBetTypes.SGP:
                return 'sgp';
            case ComboBetTypes.SGP_Plus:
                return 'sgp_plus';
        }
    }

    return 'single';
};

/**
 * Calculates the total payout amount from submitted bets
 */
const calculateTotalPayout = (submittedState: BetSubmittedState): number => {
    let total = 0;

    submittedState.singlesBets.forEach(bet => {
        const odds = submittedState.options[bet.id]?.odds ?? 1;
        const stake = bet.stake ?? 0;
        total += adjustedPayout(odds, stake, !!submittedState.isBetrBucksEnabled[bet.id]);
        total += calcProfitBoost(odds, stake, submittedState.selectedPromos[bet.id]);
    });

    if (submittedState.comboBet) {
        const { odds, bet } = submittedState.comboBet;
        const stake = bet.stake ?? 0;
        // A promo and betr bucks are mutually exclusive, so when a profit boost
        // applies the bet is always cash and comboBet.odds is the raw odds.
        total += adjustedPayout(odds, stake, !!submittedState.isBetrBucksEnabled[bet.id]);
        total += calcProfitBoost(odds, stake, submittedState.selectedPromos[bet.id]);
    }

    return total;
};

/**
 * Gets the total pick count for display
 */
const getPickCount = (submittedState: BetSubmittedState): number => {
    // For single bets, pick count is 1 per bet
    if (submittedState.singlesBets.length > 0 && !submittedState.comboBet) {
        return 1; // Single bet is always 1 pick
    }

    // For combo bets, count the selections
    if (submittedState.comboBet) {
        return submittedState.comboBet.selections.length;
    }

    return 1;
};

/**
 * Gets all bet IDs from submitted bets
 */
const getBetIds = (submittedState: BetSubmittedState): string[] => {
    return Object.values(submittedState.submittedBets).map(bet => bet.globalId);
};

/**
 * Hook to derive all necessary data for the bet success modal
 * from the store's submittedState
 */
export const useBetSuccessData = (): BetSuccessData => {
    const submittedState = useSbkBetSlipStore(state => state.submittedState);

    return useMemo(() => {
        return {
            entryType: getEntryType(submittedState),
            pickCount: getPickCount(submittedState),
            payoutAmount: calculateTotalPayout(submittedState),
            betIds: getBetIds(submittedState),
            hasCombo: !!submittedState.comboBet,
        };
    }, [submittedState]);
};
